import React, { useState } from 'react';
import { clsx } from 'clsx';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

export interface TabItem {
  /** Unique tab identifier */
  id: string;
  /** Display label (string ou node — ex.: rótulo responsivo com `sm:hidden`) */
  label: React.ReactNode;
  /** Optional icon */
  icon?: IconName;
  /** Optional badge count */
  badge?: number;
  /** Tab content */
  content: React.ReactNode;
  /** data-testid do botão da aba (F42 — elementos interativos testáveis) */
  'data-testid'?: string;
}

export interface TabsProps {
  /** Tab definitions */
  tabs: TabItem[];
  /** Initially active tab ID */
  defaultTab?: string;
  /** Visual variant */
  variant?: 'underline' | 'pills';
  /** Tabs stretch to fill available width */
  fullWidth?: boolean;
  /** Callback when tab changes */
  onChange?: (tabId: string) => void;
  /** Additional CSS classes for the tab bar */
  className?: string;
  /** Controlled active tab (overrides internal state) */
  activeTab?: string;
  /**
   * Page-level mode: adds header-matching background and centers
   * tab buttons within max-w-screen-2xl container. Use when Tabs sit directly
   * below a PageHeader on centered (non full-width) pages.
   */
  pageLevel?: boolean;
  /**
   * Only meaningful with `pageLevel`: removes the max-w-screen-2xl/mx-auto
   * centering from the tab bar so it aligns edge-to-edge with full-width
   * pages (e.g. workspaces that already render their own header full-width).
   * Default `false` preserves the centered behavior used by Account/Billing/
   * Quality Reports.
   */
  fullBleed?: boolean;
  /**
   * Esconde os ícones das abas no mobile (`< sm`), deixando só o texto — como
   * as `qtabs`/`rtabs` mobile dos mockups de relatórios. Default `false`.
   */
  hideIconsOnMobile?: boolean;
}

/**
 * Shared Tabs component with underline and pills variants.
 *
 * Use `pageLevel` when tabs appear directly below a PageHeader on
 * centered pages (Account, Billing, etc.) so the tab bar gets the
 * header background and centered alignment.
 *
 * @example
 * ```tsx
 * <Tabs
 *   tabs={[
 *     { id: 'general', label: 'Geral', icon: FiSettings, content: <GeneralTab /> },
 *     { id: 'filters', label: 'Filtros', icon: FiFilter, badge: 3, content: <FiltersTab /> },
 *   ]}
 *   defaultTab="general"
 *   variant="underline"
 *   pageLevel
 * />
 * ```
 */
export const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultTab,
  variant = 'underline',
  fullWidth = false,
  onChange,
  className,
  activeTab: controlledTab,
  pageLevel = false,
  fullBleed = false,
  hideIconsOnMobile = false,
}) => {
  const tabIconClass = hideIconsOnMobile ? 'w-4 h-4 hidden sm:inline-block' : 'w-4 h-4';
  const [internalTab, setInternalTab] = useState(defaultTab || tabs[0]?.id);
  const activeTab = controlledTab ?? internalTab;

  const handleTabClick = (tabId: string) => {
    if (controlledTab === undefined) {
      setInternalTab(tabId);
    }
    onChange?.(tabId);
  };

  const activeContent = tabs.find((t) => t.id === activeTab)?.content;

  const renderTabButtons = () =>
    tabs.map((tab) => {
      const isActive = tab.id === activeTab;
      const tabIcon = tab.icon;

      if (variant === 'pills') {
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTabClick(tab.id)}
            data-testid={tab['data-testid']}
            className={clsx(
              'flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
              fullWidth && 'flex-1'
            )}
            style={{
              backgroundColor: isActive ? 'var(--theme-primary)' : 'transparent',
              color: isActive ? '#ffffff' : 'var(--theme-muted-text)',
            }}
          >
            {tabIcon && <Icon name={tabIcon} className={tabIconClass} />}
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <span
                className="ml-1 px-1.5 py-0.5 text-xs rounded-full"
                style={{
                  backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : 'var(--theme-primary-background)',
                  color: isActive ? '#ffffff' : 'var(--theme-primary)',
                }}
              >
                {tab.badge}
              </span>
            )}
          </button>
        );
      }

      // Underline variant
      return (
        <button
          key={tab.id}
          type="button"
          onClick={() => handleTabClick(tab.id)}
          data-testid={tab['data-testid']}
          className={clsx(
            'relative flex items-center justify-center gap-1.5 px-4 py-3 text-sm font-medium transition-colors',
            fullWidth && 'flex-1'
          )}
          style={{
            color: isActive ? 'var(--theme-primary)' : 'var(--theme-muted-text)',
          }}
        >
          {tabIcon && <Icon name={tabIcon} className={tabIconClass} />}
          {tab.label}
          {tab.badge !== undefined && tab.badge > 0 && (
            <span
              className="ml-1 px-1.5 py-0.5 text-xs rounded-full"
              style={{
                backgroundColor: 'var(--theme-primary)',
                color: '#ffffff',
              }}
            >
              {tab.badge}
            </span>
          )}
          {/* Active indicator line */}
          {isActive && (
            <span
              className="absolute bottom-0 left-0 right-0 h-0.5"
              style={{ backgroundColor: 'var(--theme-primary)' }}
            />
          )}
        </button>
      );
    });

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Tab Bar */}
      {pageLevel ? (
        // Page-level: header background + centered container
        <div
          className="flex-shrink-0 border-b"
          style={{
            backgroundColor: 'var(--theme-header-background)',
            borderColor: 'var(--theme-header-border)',
          }}
        >
          <div
            className={clsx(
              'flex overflow-x-auto',
              fullBleed ? 'px-4' : 'max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8',
              variant === 'pills' && 'p-1 gap-1',
              className
            )}
          >
            {renderTabButtons()}
          </div>
        </div>
      ) : (
        // Default: inline tab bar (for sidebars, forms, etc.)
        <div
          className={clsx(
            'flex flex-shrink-0',
            variant === 'underline' && 'border-b',
            variant === 'pills' && 'p-1 gap-1',
            className
          )}
          style={
            variant === 'underline'
              ? { borderColor: 'var(--theme-card-border)' }
              : undefined
          }
        >
          {renderTabButtons()}
        </div>
      )}

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeContent}
      </div>
    </div>
  );
};

Tabs.displayName = 'Tabs';
